import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { useAuthContext } from './AuthProvider';
import { userManager } from '../../config/oidc';

// Environment-aware API bases — must match the axios baseURL used by
// organizationService/userService. Dev server proxies bare paths; the
// deployed build is served under a `/dev` prefix.
const IAM_API_BASE = import.meta.env.MODE === 'development' ? '/iam/api' : '/iam/api';
const AUTH_BASE = import.meta.env.MODE === 'development' ? '/auth' : '/auth';

// =============================================================================
// TYPES
// =============================================================================

export interface Organization {
  id: string;
  name: string;
  roles: string[];
}

export interface RBACContextType {
  // Platform roles
  isSuperAdmin: boolean;
  isPlatformSupport: boolean;

  // Organization context
  currentOrgId: string | null;
  currentOrgRoles: string[];
  organizations: Organization[];

  // Role checks
  hasOrgRole: (role: string) => boolean;
  hasAnyOrgRole: (roles: string[]) => boolean;
  canAccess: (page: string) => boolean;
  canEdit: (page: string) => boolean;

  // Org switching (super_admin only)
  switchOrganization: (orgId: string) => Promise<void>;
  /**
   * Imperatively fetch the org list from the API. For regular users
   * this hits the per-user `/auth/realms/doit/orgs/me` endpoint. For
   * super_admin / platform_support it hits the privileged
   * `/iam/api/organizations` catalog endpoint, which exposes EVERY
   * org in the realm.
   *
   * Intentionally NOT called automatically: prevents the catalog from
   * being fetched (and that data from sitting in memory) just because
   * a super_admin happens to be logged in. UI components that actually
   * need the list — e.g. the OrgSwitcher dropdown when opened, the
   * IAM page when listing orgs — should call this on demand. Calls
   * are idempotent: re-invoking after a successful fetch is a no-op
   * unless the caller wants to bypass that with `force: true`.
   */
  refreshOrganizations: (opts?: { force?: boolean }) => Promise<void>;
}

// =============================================================================
// ROLE CONSTANTS
// =============================================================================

export const REALM_ROLES = {
  SUPER_ADMIN: 'super_admin',
  PLATFORM_SUPPORT: 'platform_support',
} as const;

export const ORG_ROLES = {
  ORG_ADMIN: 'org_admin',
  TASK_ORG_ADMIN: 'task_org_admin',
  PROCESS_DESIGNER: 'process_designer',
  PROCESS_MANAGER: 'process_manager',
  TASK_USER: 'task_user',
  VIEWER: 'viewer',
} as const;

// =============================================================================
// PAGE ACCESS MATRIX
// =============================================================================

interface PageAccess {
  requiredRoles: string[];
  readOnlyRoles: string[];
}

/**
 * Page-level access matrix. `requiredRoles` get full access; `readOnlyRoles`
 * get view-only access (the ReadOnlyProvider downstream uses this to disable
 * mutation UI). Super-admins / platform-support bypass this table entirely.
 *
 * Target persona behaviour:
 *   - super_admin   → sees everything; can switch orgs; can copy templates
 *                     across orgs (handled in Studio with isSuperAdmin gate).
 *   - org_admin     → sees and edits everything in their org.
 *   - process_designer / process_manager → power users; everything except
 *     IAM and Settings.
 *   - task_user     → minimal worker role. Should see only the day-to-day
 *     panes: Analytics (read), Tasks, OCR Tracking. Explicitly NOT processes,
 *     incidents, storage, studio, tools, integrations, IAM, settings.
 *   - viewer        → read-only auditor. Sees the same data-heavy pages as
 *     process_manager but cannot mutate anything.
 */
export const PAGE_ACCESS: Record<string, PageAccess> = {
  'all-orgs': {
    requiredRoles: [REALM_ROLES.SUPER_ADMIN],
    readOnlyRoles: []
  },
  // Day-to-day worker pages — task_user is a first-class user here.
  'tasks': {
    requiredRoles: [ORG_ROLES.ORG_ADMIN, ORG_ROLES.PROCESS_DESIGNER, ORG_ROLES.PROCESS_MANAGER, ORG_ROLES.TASK_USER],
    readOnlyRoles: [ORG_ROLES.VIEWER]
  },
  'ocr-tracking': {
    requiredRoles: [ORG_ROLES.ORG_ADMIN, ORG_ROLES.PROCESS_DESIGNER, ORG_ROLES.PROCESS_MANAGER, ORG_ROLES.TASK_USER],
    readOnlyRoles: [ORG_ROLES.VIEWER]
  },
  'analytics': {
    // task_user gets read-only analytics so the worker persona can see
    // their queue stats without being able to drill into editing.
    requiredRoles: [ORG_ROLES.ORG_ADMIN, ORG_ROLES.PROCESS_DESIGNER, ORG_ROLES.PROCESS_MANAGER],
    readOnlyRoles: [ORG_ROLES.TASK_USER, ORG_ROLES.VIEWER]
  },

  // Power-user pages — task_user is intentionally absent so they're
  // hidden from the worker sidebar.
  'processes': {
    requiredRoles: [ORG_ROLES.ORG_ADMIN, ORG_ROLES.PROCESS_DESIGNER, ORG_ROLES.PROCESS_MANAGER],
    readOnlyRoles: [ORG_ROLES.VIEWER]
  },
  'studio': {
    requiredRoles: [ORG_ROLES.ORG_ADMIN, ORG_ROLES.PROCESS_DESIGNER],
    readOnlyRoles: []
  },
  // Template Engine — accessible to every authenticated org role.
  // Modelled after `tasks` / `ocr-tracking` rather than the broader
  // `studio` so other Studio sub-pages (workflows, decisions, rules,
  // forms, …) keep their original tighter restrictions.
  'template-engine': {
    requiredRoles: [
      ORG_ROLES.ORG_ADMIN,
      ORG_ROLES.PROCESS_DESIGNER,
      ORG_ROLES.PROCESS_MANAGER,
      ORG_ROLES.TASK_USER,
    ],
    readOnlyRoles: [ORG_ROLES.VIEWER],
  },
  'tools': {
    requiredRoles: [ORG_ROLES.ORG_ADMIN, ORG_ROLES.PROCESS_DESIGNER],
    readOnlyRoles: []
  },
  'incidents': {
    requiredRoles: [ORG_ROLES.ORG_ADMIN, ORG_ROLES.PROCESS_DESIGNER, ORG_ROLES.PROCESS_MANAGER],
    readOnlyRoles: [ORG_ROLES.VIEWER]
  },
  'storage': {
    // task_user removed: workers don't manage MinIO buckets directly.
    requiredRoles: [ORG_ROLES.ORG_ADMIN, ORG_ROLES.PROCESS_DESIGNER, ORG_ROLES.PROCESS_MANAGER],
    readOnlyRoles: [ORG_ROLES.VIEWER]
  },
  'integrations': {
    requiredRoles: [ORG_ROLES.ORG_ADMIN, ORG_ROLES.PROCESS_DESIGNER],
    readOnlyRoles: []
  },

  // Admin pages.
  'iam': {
    // task_org_admin is an org-scoped admin role that also manages IAM —
    // granted full access so the IAM item shows in the sidebar and the
    // /iam route (Guarded page="iam") opens instead of Access Denied.
    requiredRoles: [ORG_ROLES.ORG_ADMIN, ORG_ROLES.TASK_ORG_ADMIN],
    readOnlyRoles: []
  },
  'settings': {
    requiredRoles: [ORG_ROLES.ORG_ADMIN],
    readOnlyRoles: []
  },
};

// =============================================================================
// CONTEXT
// =============================================================================

const RBACContext = createContext<RBACContextType | undefined>(undefined);

export const useRBAC = (): RBACContextType => {
  const context = useContext(RBACContext);
  if (!context) {
    throw new Error('useRBAC must be used within an RBACProvider');
  }
  return context;
};

// =============================================================================
// PROVIDER
// =============================================================================

interface RBACProviderProps {
  children: React.ReactNode;
}

export const RBACProvider: React.FC<RBACProviderProps> = ({ children }) => {
  const { user, isAuthenticated, getAccessToken } = useAuthContext();
  const [apiOrganizations, setApiOrganizations] = React.useState<Organization[]>([]);
  const [, setIsLoadingOrgs] = React.useState(false);

  // Extract realm roles from token
  const realmRoles = useMemo((): string[] => {
    if (!user?.access_token) return [];
    try {
      const payload = JSON.parse(atob(user.access_token.split('.')[1]));
      return payload.realm_access?.roles || [];
    } catch {
      return [];
    }
  }, [user?.access_token]);

  /**
   * Roles scoped to specific OIDC clients in `resource_access.<client>.roles`.
   * Some Keycloak setups attach application roles (task_user, viewer, etc.)
   * here rather than at the realm or org level — we collect them all into
   * one flat set so canAccess sees them regardless of where the admin put
   * the role assignment.
   */
  const clientRoles = useMemo((): string[] => {
    if (!user?.access_token) return [];
    try {
      const payload = JSON.parse(atob(user.access_token.split('.')[1]));
      const resourceAccess = payload.resource_access || {};
      const collected = new Set<string>();
      for (const entry of Object.values(resourceAccess)) {
        const roles = (entry as { roles?: string[] })?.roles;
        if (Array.isArray(roles)) {
          for (const role of roles) collected.add(role);
        }
      }
      return Array.from(collected);
    } catch {
      return [];
    }
  }, [user?.access_token]);

  const isSuperAdmin = useMemo(
    () => realmRoles.includes(REALM_ROLES.SUPER_ADMIN),
    [realmRoles]
  );

  const isPlatformSupport = useMemo(
    () => realmRoles.includes(REALM_ROLES.PLATFORM_SUPPORT),
    [realmRoles]
  );

  // Extract current org ID from token. Keycloak's Phase-Two extension can
  // store this in several shapes:
  //   - Plain string:        "uuid"
  //   - Array of ids:        ["uuid", ...]
  //   - Rich object:         { id: "uuid", role: [...] }
  //   - Stringified map:     "{uuid={role=[...]}}"  or "{id=uuid,...}"
  // We normalise all four to a single string id (or null).
  const currentOrgId = useMemo((): string | null => {
    if (!user?.access_token) return null;
    try {
      const payload = JSON.parse(atob(user.access_token.split('.')[1]));
      let orgId: any = payload.org_id;

      // Array form: [{...}] or ["uuid"] — take the first element.
      if (Array.isArray(orgId)) {
        orgId = orgId[0];
      }

      // Rich object form: { id, role }
      if (typeof orgId === 'object' && orgId !== null && 'id' in orgId) {
        orgId = orgId.id;
      }

      // Stringified map forms.
      if (typeof orgId === 'string' && orgId.startsWith('{')) {
        if (orgId.includes('id=')) {
          try {
            const idIndex = orgId.indexOf('id=') + 3;
            const endIndex = orgId.indexOf(',', idIndex);
            const end = endIndex !== -1 ? endIndex : orgId.indexOf('}', idIndex);
            if (end !== -1) {
              orgId = orgId.substring(idIndex, end).trim();
            }
          } catch (e) {
            console.warn('Failed to parse active org id', e);
          }
        } else if (orgId.includes('=')) {
          try {
            const equalsIndex = orgId.indexOf('=');
            orgId = orgId.substring(1, equalsIndex).trim();
          } catch (e) {
            console.warn('Failed to parse complex org_id:', e);
          }
        }
      }

      return typeof orgId === 'string' && orgId.length > 0 ? orgId : null;
    } catch {
      return null;
    }
  }, [user?.access_token]);

  // Organizations directly from the JWT (preferred when present).
  const tokenOrganizations = useMemo((): Organization[] => {
    if (!user?.access_token) return [];
    try {
      const payload = JSON.parse(atob(user.access_token.split('.')[1]));
      const orgs = payload.organizations || {};
      return Object.entries(orgs).map(([id, data]: [string, any]) => ({
        id,
        name: data.name || id,
        roles: data.roles || [],
      }));
    } catch {
      return [];
    }
  }, [user?.access_token]);

  // Fallback: fetch the org list from the IAM/Phase-Two API. Super admins
  // and platform support always hit the IAM list endpoint to get the full
  // catalogue (the token only carries the orgs they're currently joined to).
  /**
   * Reset the cached API org list whenever the user changes (logout
   * → login, account switch). Bound to the user's `sub` claim rather
   * than the access token so silent renewals don't flush it.
   */
  const userSubRef = React.useRef<string | null>(null);
  const currentSub = React.useMemo(() => {
    if (!user?.access_token) return null;
    try {
      const payload = JSON.parse(atob(user.access_token.split('.')[1]));
      return typeof payload.sub === 'string' ? payload.sub : null;
    } catch {
      return null;
    }
  }, [user?.access_token]);
  React.useEffect(() => {
    if (userSubRef.current && userSubRef.current !== currentSub) {
      setApiOrganizations([]);
    }
    userSubRef.current = currentSub;
  }, [currentSub]);

  // task_org_admin is a realm role — PhaseTwo does NOT inject it into
  // the `organizations` JWT claim, so tokenOrganizations is always empty
  // for these users. We need to detect it early so refreshOrganizations
  // can route them to the IAM API (which now scopes its response to their
  // own org) instead of the per-user /orgs/me endpoint.
  const isTaskOrgAdmin = useMemo(
    () => realmRoles.includes(ORG_ROLES.TASK_ORG_ADMIN),
    [realmRoles]
  );

  /**
   * Imperative org-list fetch. See the type definition above for the
   * full rationale — short version: this is deliberately NOT called
   * from a useEffect, so the privileged catalog endpoint
   * (`/iam/api/organizations`) is never hit just because a super_admin
   * is logged in. UI components that actually need the list call this
   * when the user expresses intent (opens the org switcher, navigates
   * to the IAM listing, etc.).
   */
  const refreshOrganizations = useCallback(
    async (opts?: { force?: boolean }): Promise<void> => {
      if (!isAuthenticated || !user?.access_token) return;
      // Regular users with org info already in the JWT don't need the
      // API at all — `tokenOrganizations` is authoritative.
      // Note: task_org_admin is a realm role, NOT an org-level role, so
      // PhaseTwo never puts it in the `organizations` JWT claim. We skip
      // the early-return for them so the API path is always taken.
      if (!isSuperAdmin && !isPlatformSupport && !isTaskOrgAdmin && tokenOrganizations.length > 0) {
        return;
      }
      // Idempotent: once we have a list cached for this user, don't
      // re-fetch unless the caller explicitly opts in.
      if (!opts?.force && apiOrganizations.length > 0) {
        return;
      }

      setIsLoadingOrgs(true);
      try {
        // Pick the endpoint by role. We deliberately NEVER call the privileged
        // /organizations CATALOG for org-scoped users — it lists every org in
        // the realm and 403s for non-privileged roles. Instead:
        //   - super_admin / platform_support → catalog (size=1000)
        //   - task_org_admin → their OWN org only, via the specific-org
        //     endpoint /organizations/{orgId} (any authenticated user)
        //   - everyone else → per-user PhaseTwo /orgs/me
        const privileged = isSuperAdmin || isPlatformSupport;
        let url: string;
        let mode: 'catalog' | 'single' | 'me';
        if (privileged) {
          url = `${IAM_API_BASE}/organizations?search=&page=0&size=1000`;
          mode = 'catalog';
        } else if (isTaskOrgAdmin && currentOrgId) {
          url = `${IAM_API_BASE}/organizations/${currentOrgId}`;
          mode = 'single';
        } else {
          url = `${AUTH_BASE}/realms/doit/orgs/me`;
          mode = 'me';
        }

        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${user.access_token}` },
        });

        if (response.ok) {
          const data = await response.json();
          let orgs: Organization[] = [];

          if (mode === 'catalog') {
            // catalog returns { content: [...], count, page, size }
            const content = data.content || [];
            orgs = content.map((org: any) => ({
              id: org.id,
              name: org.name || org.displayName || org.id,
              roles: [],
            }));
          } else if (mode === 'single') {
            // /organizations/{orgId} returns a single OrganizationRepresentation
            if (data && data.id) {
              orgs = [{
                id: data.id,
                name: data.name || data.displayName || data.id,
                roles: [],
              }];
            }
          } else {
            // /auth/realms/doit/orgs/me returns { "<orgId>": { name, roles, ... } }
            orgs = Object.entries(data).map(([id, orgData]: [string, any]) => ({
              id,
              name: orgData.name || orgData.displayName || id,
              roles: orgData.roles || [],
            }));
          }
          setApiOrganizations(orgs);
        }
      } catch (error) {
        console.error('Failed to fetch organizations from API:', error);
      } finally {
        setIsLoadingOrgs(false);
      }
    },
    [
      isAuthenticated,
      user?.access_token,
      tokenOrganizations.length,
      isSuperAdmin,
      isPlatformSupport,
      isTaskOrgAdmin,
      currentOrgId,
      apiOrganizations.length,
    ],
  );

  // Prefer JWT for regular users; prefer API for super admin / platform support.
  const organizations = useMemo((): Organization[] => {
    if ((isSuperAdmin || isPlatformSupport) && apiOrganizations.length > 0) {
      return apiOrganizations;
    }
    return tokenOrganizations.length > 0 ? tokenOrganizations : apiOrganizations;
  }, [tokenOrganizations, apiOrganizations, isSuperAdmin, isPlatformSupport]);

  // Roles for the active org. Prefer the inline `org_id.role` array in the
  // token (newer Keycloak shape); fall back to looking up the org in the list.
  const currentOrgRoles = useMemo((): string[] => {
    if (!user?.access_token) return [];
    try {
      const payload = JSON.parse(atob(user.access_token.split('.')[1]));
      const orgIdPayload = payload.org_id;
      if (
        typeof orgIdPayload === 'object' &&
        orgIdPayload !== null &&
        Array.isArray(orgIdPayload.role)
      ) {
        return orgIdPayload.role;
      }
    } catch (e) {
      console.warn('Failed to parse roles from org_id payload', e);
    }

    if (currentOrgId) {
      const org = organizations.find((o) => o.id === currentOrgId);
      return org?.roles || [];
    }
    if (organizations.length > 0) {
      return organizations[0].roles;
    }
    return [];
  }, [currentOrgId, organizations, user?.access_token]);

  /**
   * Effective role set used by canAccess / canEdit. Keycloak setups vary
   * by where the admin attaches role assignments — `realm_access.roles`,
   * `resource_access.<client>.roles`, or `organizations.<id>.roles`. We
   * match against the UNION of all three so a single role on any of those
   * surfaces resolves correctly, no Keycloak migration required.
   */
  const effectiveRoles = useMemo(
    () => Array.from(new Set([...realmRoles, ...clientRoles, ...currentOrgRoles])),
    [realmRoles, clientRoles, currentOrgRoles]
  );

  // Diagnostics. Logs a compact snapshot every time the resolved state
  // changes, and exposes window.__doitRbac() / window.__doitRbacToken()
  // for deeper inspection in DevTools when something's not adding up.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const snapshot = {
      isAuthenticated,
      isSuperAdmin,
      isPlatformSupport,
      currentOrgId,
      realmRoles,
      clientRoles,
      currentOrgRoles,
      effectiveRoles,
      organizationCount: organizations.length,
    };
    // eslint-disable-next-line no-console
    console.log('[RBAC]', snapshot);
    (window as any).__doitRbac = () => snapshot;
    // Cracked-open JWT view — handy when roles aren't where you expect.
    // Returns the decoded token payload so you can see the raw claim
    // names (organizations, resource_access, realm_access, org_id, …).
    (window as any).__doitRbacToken = () => {
      try {
        const tok = user?.access_token;
        if (!tok) return null;
        return JSON.parse(atob(tok.split('.')[1]));
      } catch (e) {
        return { error: String(e) };
      }
    };
  }, [
    isAuthenticated,
    isSuperAdmin,
    isPlatformSupport,
    currentOrgId,
    realmRoles,
    clientRoles,
    currentOrgRoles,
    effectiveRoles,
    organizations.length,
    user?.access_token,
  ]);

  const hasOrgRole = useCallback(
    (role: string): boolean => {
      if (isSuperAdmin) return true;
      return effectiveRoles.includes(role);
    },
    [isSuperAdmin, effectiveRoles]
  );

  const hasAnyOrgRole = useCallback(
    (roles: string[]): boolean => {
      if (isSuperAdmin) return true;
      return roles.some((role) => effectiveRoles.includes(role));
    },
    [isSuperAdmin, effectiveRoles]
  );

  const canAccess = useCallback(
    (page: string): boolean => {
      if (!isAuthenticated) return false;
      if (isSuperAdmin) return true;
      if (isPlatformSupport) return true;

      const access = PAGE_ACCESS[page];
      if (!access) return true;

      const hasFullAccess = access.requiredRoles.some((role) =>
        effectiveRoles.includes(role)
      );
      const hasReadOnly = access.readOnlyRoles.some((role) =>
        effectiveRoles.includes(role)
      );
      return hasFullAccess || hasReadOnly;
    },
    [isAuthenticated, isSuperAdmin, isPlatformSupport, effectiveRoles]
  );

  const canEdit = useCallback(
    (page: string): boolean => {
      if (!isAuthenticated) return false;
      if (isSuperAdmin) return true;
      if (isPlatformSupport) return false; // platform_support is read-only

      const access = PAGE_ACCESS[page];
      if (!access) return true;
      return access.requiredRoles.some((role) => effectiveRoles.includes(role));
    },
    [isAuthenticated, isSuperAdmin, isPlatformSupport, effectiveRoles]
  );

  const switchOrganization = useCallback(
    async (orgId: string): Promise<void> => {
      const token = getAccessToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`${IAM_API_BASE}/organizations/switch/${orgId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to switch organization');
      }

      // The new active org lives in a freshly-issued token. Trigger a
      // silent re-auth so the rest of the app sees the new `org_id` claim
      // on the next render. If that fails, fall back to a full reload.
      try {
        await userManager.signinSilent();
      } catch (err) {
        console.warn('[rbac] signinSilent after org switch failed, reloading', err);
        window.location.reload();
      }
    },
    [getAccessToken]
  );

  const contextValue: RBACContextType = useMemo(
    () => ({
      isSuperAdmin,
      isPlatformSupport,
      currentOrgId,
      currentOrgRoles,
      organizations,
      hasOrgRole,
      hasAnyOrgRole,
      canAccess,
      canEdit,
      switchOrganization,
      refreshOrganizations,
    }),
    [
      isSuperAdmin,
      isPlatformSupport,
      currentOrgId,
      currentOrgRoles,
      organizations,
      hasOrgRole,
      hasAnyOrgRole,
      canAccess,
      canEdit,
      switchOrganization,
      refreshOrganizations,
    ]
  );

  return <RBACContext.Provider value={contextValue}>{children}</RBACContext.Provider>;
};

// =============================================================================
// READ ONLY CONTEXT (for child components)
// =============================================================================

const ReadOnlyContext = createContext<boolean>(false);

export const useReadOnly = (): boolean => useContext(ReadOnlyContext);

export const ReadOnlyProvider: React.FC<{
  isReadOnly: boolean;
  children: React.ReactNode;
}> = ({ isReadOnly, children }) => (
  <ReadOnlyContext.Provider value={isReadOnly}>{children}</ReadOnlyContext.Provider>
);

export default RBACProvider;
